import Big from "big.js";

declare global {
    interface URLSearchParams {
        cleanURLParams(): URLSearchParams;
    }

    interface Array<T> {
        sortTags(): Array<T>
    }

    interface Response {
        enzianJSON(): Promise<any>
    }
}

declare module 'big.js' {
    export interface Big {
        max(maxValue: import('big.js').Big): import('big.js').Big
    }
}

Response.prototype.enzianJSON = async function (): Promise<any> {
    const text = await this.text();

    //Handle empty requests
    if (text.length <= 0) return

    return JSON.parse(text, function (key, val) {
        //if this is an object, and has the _big_ property, return it as big instance
        if (typeof (val) === 'object' && val?.hasOwnProperty("_big_")) {
            return new Big(val._big_);
        }
        return val;
    })
}

/**
 * Maps a list of params to an URLSearchParams object which can be used to add to the url
 */
URLSearchParams.prototype.cleanURLParams = function () {

    let keysForDel: string[] = [];
    this.forEach((value, key) => {

        let vals = value.split("|")

        let lookAt = vals[0]
        if (vals.length > 1) {
            lookAt = vals[1]
        }

        if (lookAt == 'undefined' || lookAt == '' || lookAt == 'null' || lookAt == undefined) {
            keysForDel.push(key);
        }
    });

    keysForDel.forEach(key => {
        this.delete(key);
    });

    return this
}


export function debounce(func: any, wait: number = 500) {
    // 'private' variable for instance
    // The returned function will be able to reference this due to closure.
    // Each call to the returned function will share this common timer.
    var timeout: NodeJS.Timeout | null;

    // Calling debounce returns a new anonymous function
    return function (this: any, ...args: any[]) {
        // reference the context and args for the setTimeout function

        var context = this

        // This is the basic debounce behaviour where you can call this
        //   function several times, but it will only execute once
        //   (before or after imposing a delay).
        //   Each time the returned function is called, the timer starts over.
        if (timeout) {
            clearTimeout(timeout);
        }

        // Set the new timeout
        timeout = setTimeout(function () {

            // Inside the timeout function, clear the timeout variable
            timeout = null;

            // Call the original function with apply
            // apply lets you define the 'this' object as well as the arguments
            //    (both captured before setTimeout)
            func.apply(context, args);

        }, wait);

    }
}

/**
 * Create a promise which will retry itself
 * @operation The funtion to be called.
 * @retries The amount of retries that should be done
 * @delay The delay in ms after each failed call
 */
export function createRetryFunction<T>(fn: (...args: any[]) => Promise<T>, attempts = 3, delay = 2000): (...args: any[]) => Promise<T> {
    //@ts-ignore
    return async function (this: any, ...args): Promise<T> {
        for (let i = attempts; i > 0; i--) {
            try {
                return await fn.call(this, ...args); // delegate
            } catch (e) {
                if (i > 1) await new Promise(r => setTimeout(r, delay));
                else {
                    throw e;
                }
            }
        }
    }
}

/**
 * 
 * @param millis 
 * @returns A promise which will resolve in X millis.
 */
export function asyncWaitForMillis(millis: number) {
    return new Promise(res => setTimeout(res, millis))
}

/**
 * Function which can be used if you want to check if the click was done outside
 * @param node 
 * @returns 
 */
export function clickOutside(node: HTMLElement, callbackFunction: () => void) {

    const handleClick = (event: MouseEvent) => {
        //@ts-ignore
        if (node && !node.contains(event.target) && !event.defaultPrevented) {
            callbackFunction()
        }
    }

    document.addEventListener('click', handleClick, true);

    return {
        destroy() {
            document.removeEventListener('click', handleClick, true);
        }
    }
}

/**
 * Used to add event listener on the document for the esc key
 * @param node 
 * @param callbackFunction 
 * @returns 
 */
export function pressESC(node: HTMLElement, callbackFunction: () => void) {

    const handleKeydown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            callbackFunction()
        }
    }

    document.addEventListener('keydown', handleKeydown, true);

    return {
        destroy() {
            document.removeEventListener('keydown', handleKeydown, true);
        }
    }

}


export function BigMax(arg1: Big, arg2: Big): Big {
    if (arg1.lt(arg2)) {
        return arg2
    } else {
        return arg1
    }
}

export function BigMin(arg1: Big, arg2: Big): Big {
    if (arg1.gt(arg2)) {
        return arg2
    } else {
        return arg1
    }
}

Array.prototype.sortTags = function () {
    return this.toSorted((a, b) => {
        return ("" + a).localeCompare(b);
    })
}

/**
 * Returns the currently set locale to the store and falls back to de-CH
 * @returns 
 */
export function getLocaleWithFallback(): string {
    return "de-CH"
    //return get(locale) || "de"
}

/**
 * @returns A locale with country for the print setting.
 */
export function getDefaultLocaleForPrint(): string {
    let lang = getLocaleWithFallback();
    if (lang == "it") {
        return "it-CH"
    } else if (lang == "fr") {
        return "fr-CH"
    } else if (lang == "en") {
        return "en-CH"
    } else {
        return "de-CH"
    }
}


/**
 * Parses a version string (e.g. "1.2.3") into numeric tuple [major, minor, patch]
 * matching the SQL: REGEXP_REPLACE(COALESCE(NULLIF(string_to_array(val, '.')[n], ''), '0'), '[^0-9]', '0', 'g')::numeric
 * Same behaviour as backend.
 */
function parseVersionPart(part: string) {
  if (part === undefined || part === '') return 0;
  const cleaned = part.replace(/[^0-9]/g, '0');
  return cleaned === '' ? 0 : Number(cleaned);
}

/**
 * Converts a version string into a [major, minor, patch] numeric tuple
 * for comparison, mirroring the SQL ordering expression.
 *
 * @param {string} value - e.g. "1.2.3", "2.0", "1.10.0-beta"
 * @returns {[number, number, number]}
 */
function toVersionTuple(value: string) {
  const parts = (value ?? '').split('.');
  return [
    parseVersionPart(parts[0]),
    parseVersionPart(parts[1]),
    parseVersionPart(parts[2]),
  ];
}

/**
 * Comparator function matching the SQL ORDER BY expression.
 * Use with Array.prototype.sort().
 *
 * @param {string} a
 * @param {string} b
 * @returns {number} negative, 0, or positive
 */
export function versionComparator(a: string, b: string): number {
  const [aMaj, aMin, aPatch] = toVersionTuple(a);
  const [bMaj, bMin, bPatch] = toVersionTuple(b);

  return aMaj - bMaj || aMin - bMin || aPatch - bPatch;
}